import {
  TextField,
  FlexColumn,
  Loading,
  Alert,
} from '@elementary/common-ui/components';
import { formFieldRequiredLabel as requiredLabel } from '@elementary/common-ui/components';
import styled from 'styled-components';
import { MenuItem, Select, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { Role } from 'settings/components/shared/types';
import { useRoles } from '../../shared/useRoles';
import { GroupFormData } from '../types';

const Tab = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 0;
`;

const FieldContainer = styled(FlexColumn)`
  gap: 0;
  position: relative;
`;

const FieldLabel = styled(Typography)`
  position: absolute;
  top: -9px;
  left: 14px;
  background: white;
  padding: 0 2px;
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.common.black};
  z-index: 1;
`;

const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-root {
    border-radius: 8px;
    min-height: 56px;
  }
  .MuiInputLabel-root {
    display: none;
  }
`;

const StyledTextArea = styled(TextField)`
  .MuiOutlinedInput-root {
    border-radius: 8px;
    min-height: 136px;
    align-items: flex-start;
    padding-top: 16.5px;
  }
  .MuiInputLabel-root {
    display: none;
  }
  textarea {
    resize: none;
  }
`;

const SelectedRolesValue = ({
  selected,
  availableRoles,
}: {
  selected: string[];
  availableRoles?: Role[];
}) => {
  if (selected.length === 0) {
    return <Typography sx={{ color: 'common.gray2' }}>Select roles</Typography>;
  }
  return selected
    .map((roleId) => availableRoles?.find((role) => role.id === roleId)?.name)
    .filter(Boolean)
    .join(', ');
};

type Props = {
  hasExternalManager: boolean;
};

const ConfigurationTab = ({ hasExternalManager }: Props) => {
  const { control, register } = useFormContext<GroupFormData>();
  const { roles: availableRoles, isLoading: isRolesLoading } = useRoles();

  if (isRolesLoading) {
    return <Loading />;
  }

  return (
    <Tab>
      {hasExternalManager && (
        <Alert severity="info">
          This group is managed externally. Only role assignments can be
          modified.
        </Alert>
      )}

      <FieldContainer>
        <FieldLabel>Name</FieldLabel>
        <StyledTextField
          {...register('name', { required: requiredLabel })}
          fullWidth
          size="large"
          placeholder="e.g. Engineering team"
          disabled={hasExternalManager}
        />
      </FieldContainer>

      <FieldContainer>
        <FieldLabel>Description</FieldLabel>
        <Controller
          control={control}
          name="description"
          render={({ field }) => (
            <StyledTextArea
              {...field}
              value={field.value || ''}
              onChange={(e) => field.onChange(e.target.value || null)}
              fullWidth
              multiline
              rows={4}
              placeholder={
                hasExternalManager
                  ? 'No description'
                  : 'Enter group description'
              }
              disabled={hasExternalManager}
            />
          )}
        />
      </FieldContainer>

      <FieldContainer>
        <FieldLabel>Role</FieldLabel>
        <Controller
          control={control}
          name="roles"
          render={({ field }) => (
            <Select
              {...field}
              multiple
              displayEmpty
              renderValue={(selected: string[]) => (
                <SelectedRolesValue
                  selected={selected}
                  availableRoles={availableRoles}
                />
              )}
            >
              {availableRoles?.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  <FlexColumn sx={{ gap: '8px' }}>
                    <Typography>{role.name}</Typography>
                    {role.description && (
                      <Typography
                        sx={{ color: 'common.gray2', fontSize: '12px' }}
                      >
                        {role.description}
                      </Typography>
                    )}
                  </FlexColumn>
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </FieldContainer>
    </Tab>
  );
};

ConfigurationTab.displayName = 'ConfigurationTab';

export default ConfigurationTab;
